<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue'
  import {
    PhoneNumbersService,
    OrganisationsService,
    RetellAgentsService,
    type SuperUserPhoneNumber as PhoneNumber,
    type SuperUserRetellAgent as RetellAgent,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import type { GenericObject } from 'vee-validate'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { PhoneIcon, ClipboardIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import { Label } from '@/components/ui/label'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'

  const { t } = useTranslation({
    en: {
      active: 'Active',
      retellAgent: 'Retell agent',
      customer: 'Customer',
      phoneNumber: 'Phone number',
      copy: 'Copy',
      deactivationWarning:
        'Warning: Deactivating this phone number will deregister it from the phone provider. It will no longer be available for incoming calls. This action can not be undone.',
      inactiveInfo:
        'This phone number is inactive. It can not be reactivated, please create a new phone number for the customer.',
      description: 'Description',
      lawFirmName: 'Law Firm Name',
      address: 'Address',
      opening_hours: 'Opening Hours',
      lawyer_names: 'Lawyer Names',
      has_free_initial_consultation: 'Has free initial consultation',
      begin_message: 'Welcome message',
      end_message: 'Goodbye message'
    },
    de: {
      active: 'Aktiv',
      retellAgent: 'Retell Agent',
      customer: 'Kunde',
      phoneNumber: 'Telefonnummer',
      copy: 'Kopieren',
      deactivationWarning:
        'Warnung: Durch das Deaktivieren dieser Telefonnummer wird sie vom Telefonanbieter abgemeldet. Sie ist nicht mehr für eingehende Anrufe verfügbar. Diese Aktion kann nicht rückgängig gemacht werden.',
      inactiveInfo:
        'Diese Telefonnummer ist inaktiv. Sie kann nicht reaktiviert werden, bitte eine neue Telefonnummer für den Kunden erstellen.',
      description: 'Beschreibung',
      lawFirmName: 'Kanzleiname',
      address: 'Adresse',
      opening_hours: 'Öffnungszeiten',
      lawyer_names: 'Anwälte',
      has_free_initial_consultation: 'Erstberatung',
      begin_message: 'Begrüßungsnachricht',
      end_message: 'Abschiedsnachricht'
    }
  })

  const route = useRoute()
  const phoneNumber = ref<PhoneNumber | null>(null)
  const defaultBeginMessage = computed(() => {
    return `Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der ${phoneNumber.value.lawfirm_name}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können.
Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen.
Wobei können wir behilflich sein ?
    `
  })

  const retellAgentsList = ref<RetellAgent[] | null>(null)
  const retellAgentOptions = computed<Option[]>(() => {
    return (
      retellAgentsList.value?.map((retellAgent) => {
        return {
          value: retellAgent.uuid,
          title:
            retellAgent.agent_name +
            ' - ' +
            retellAgent.voice_id +
            ' (' +
            retellAgent.retell_agent_id +
            ')'
        }
      }) ?? []
    )
  })

  const organisationsList = ref<Organisation[] | null>(null)
  const organisationOptions = computed<Option[]>(() => {
    return (
      organisationsList.value?.map((organisation) => {
        return {
          value: organisation.uuid,
          title: organisation.name
        }
      }) ?? []
    )
  })

  onMounted(async () => {
    phoneNumber.value = (await PhoneNumbersService.phoneNumbersRetrieve({
      uuid: route.params.uuid.toString()
    })) as PhoneNumber
    retellAgentsList.value = (await RetellAgentsService.retellAgentsList()).results as RetellAgent[]
    organisationsList.value = (await OrganisationsService.organisationsList())
      .results as Organisation[]
  })

  const handleSave = async (values: GenericObject) => {
    await PhoneNumbersService.phoneNumbersPartialUpdate({
      uuid: phoneNumber.value!.uuid!,
      requestBody: values
    })
  }

  const copyPhoneNumberToClipboard = () => {
    navigator.clipboard.writeText(phoneNumber.value!.phone_number as string)
  }
</script>
<template>
  <div v-if="phoneNumber">
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST },
            {
              title: `Details - ${phoneNumber.uuid}`,
              to: ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', phoneNumber.uuid)
            }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2"></div>
      </div>
      <div
        class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="w-full md:w-1/2">
          <AutoSavingForm @save="handleSave">
            <span
              v-if="phoneNumber.active"
              class="text-sm text-yellow-600"
            >
              {{ t('deactivationWarning') }}
            </span>
            <span
              v-else
              class="text-sm text-red-600"
            >
              {{ t('inactiveInfo') }}
            </span>
            <CheckboxField
              v-model="phoneNumber.active"
              :label="t('active')"
              name="active"
              :rules="yup.boolean().oneOf([true, false])"
              :disabled="!phoneNumber.active"
            />
            <label class="mb-2 block text-sm font-medium">{{ t('phoneNumber') }}</label>
            <div class="relative flex rounded-lg shadow-sm">
              <input
                type="text"
                :value="phoneNumber.phone_number"
                class="block w-full rounded-s-lg border-gray-200 bg-gray-100 px-4 py-3 ps-11 text-sm shadow-sm focus:z-10
                  focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50
                  dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:placeholder-neutral-500
                  dark:focus:ring-neutral-600"
                disabled
              />
              <div
                class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-4"
              >
                <PhoneIcon class="size-4 shrink-0 text-gray-400 dark:text-neutral-500" />
              </div>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-x-2 rounded-e-md border border-transparent bg-purple-600
                  px-4 py-3 text-sm font-semibold text-white hover:bg-purple-700 focus:bg-purple-700
                  focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                @click="copyPhoneNumberToClipboard"
              >
                <ClipboardIcon class="size-4 shrink-0 text-white" />
                {{ t('copy') }}
              </button>
            </div>
            <SelectField
              v-model="phoneNumber.org.uuid"
              :label="t('customer')"
              :is-filterable="true"
              name="org_uuid"
              :rules="yup.string().uuid().required()"
              :options="organisationOptions"
            />

            <SelectField
              v-model="phoneNumber.agent.uuid"
              :label="t('retellAgent')"
              :is-filterable="false"
              name="agent_uuid"
              :rules="yup.string().uuid().required()"
              :options="retellAgentOptions"
            />
            <InputField
              v-model="phoneNumber.description"
              :label="t('Description')"
              name="description"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier kann eine interne Bezeichnung für die Rufnummer hinterlegt werden.
            </p>
            <InputField
              v-model="phoneNumber.lawfirm_name"
              :label="t('Law Firm Name')"
              name="lawfirm_name"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier wird der Name der Kanzlei angegeben, wie er in der Begrüßungsansage genannt
              werden soll.
            </p>
            <InputField
              v-model="phoneNumber.original_phone_number"
              :label="t('Phonenumber')"
              name="original_phone_number"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Wenn Sie die von uns generierte Nummer intern weiterleiten, geben Sie hier unbedingt
              die Originalnummer an, welche von den Mandanten angerufen wird. So stellen Sie sicher,
              dass wir die Anrufe korrekt zuordnen können.
            </p>
            <CheckboxField
              v-model="phoneNumber.has_free_initial_consultation"
              :label="t('has free initial consultation')"
              name="has_free_initial_consultation"
            />
            <p
              class="text-grey-200 text-sm font-light"
              style="padding-left: 30px"
            >
              Wenn eine kostenlose Erstberatung angeboten wird, sollte das hier aktiviert werden.
              Die KI kann dann darauf hinweisen, dass die Erstberatung kostenlos ist.
            </p>
            <TextAreaField
              v-model="phoneNumber.address"
              :label="t('Address')"
              name="address"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier kann die Adresse der Kanzlei angegeben werden. Bei Nachfrage geben wir diese an
              den Anrufer weiter. Wenn Sie keine unangekündigten Mandantenbesuch wünschen, dann
              geben Sie hier bitte einen entsprechenden Hinweis.
            </p>
            <TextAreaField
              v-model="phoneNumber.opening_hours"
              :label="t('Opening Hours')"
              name="opening_hours"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier können die Öffnungszeiten eingetragen werden. Diese können bei Nachfrage von der
              KI-Wiedergegeben werden.
            </p>
            <TextAreaField
              v-model="phoneNumber.lawyer_names"
              :label="t('Lawyer Names')"
              name="lawyer_names"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier werden die Namen der Anwälte eingetragen. Damit die KI diese besser erkennt und
              vom Anrufernamen unterscheiden kann. Hier nur den Namen eintragen und keine weitere
              Bezeichnung wie zum Beispiel Anwalt oder Sachbearbeiter.
            </p>
            <TextAreaField
              v-model="phoneNumber.begin_message"
              :value="phoneNumber.begin_message ?? defaultBeginMessage"
              :label="t('begin_message')"
              name="begin_message"
              :rules="yup.string()"
              :placeholder="defaultBeginMessage"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier kann eine Begrüßungsnachricht für die Kanzlei hinterlegt werden. Diese wird vorab
              an den Anrufer gespielt, bevor der Anruf angenommen wird.
            </p>
            <TextAreaField
              v-model="phoneNumber.end_message"
              :value="phoneNumber.end_message ?? ''"
              :label="t('end_message')"
              name="end_message"
              :rules="yup.string()"
            />
            <p class="text-grey-200 text-sm font-light">
              Hier kann eine Abschiedsnachricht für die Kanzlei hinterlegt werden. Diese wird vorab
              an den Anrufer gespielt, bevor der Anruf angenommen wird.
            </p>
          </AutoSavingForm>
        </div>
      </div>
    </div>
  </div>
</template>
